import "./style.scss";

import { Button } from "@mui/material";
import variables from "assets/styles/_variables.module.scss";
import { useCompanySettings } from "hooks/useCompanySettings";
import type { ChangeEvent } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IAdvanceSlice } from "store/advancePaymentSlice";
import { IArticle } from "store/articleStore";
import { IFile } from "store/filesHistorySlice";
import { GeneralTabTerm, ISalesByChannels, ITerm, ITermBeneficiar } from "store/managementStore";
import { IPayment, IPaymentInfo } from "store/paymentSlice";
import type { TPortalType } from "store/types";
import { ILastStatement, TStock } from "store/types";
import { ILog } from "types";

import { StyledMenuItem, StyledSelect } from "../StyledSelect";

type TProps = {
    rowsPerPageOptions: number[];
    rowsPerPage: number;
    currentPageSize?: number;
    page: number;
    onPageChange: (event: unknown, newPage: number) => void;
    onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    type:
        | "terms"
        | "beneficiaries"
        | "titles"
        | "payment"
        | "paymentInfo"
        | "files"
        | "sales"
        | "statements"
        | "publications"
        | "articles"
        | "articles_audiovisual"
        | "apiKeys"
        | "logs"
        | "exports"
        | "paymentExports"
        | "brands"
        | "contracts"
        | "users"
        | "transferTerms"
        | "transferTermSales"
        | "transferTermAdvances"
        | "transfereeTerms"
        | "representatives"
        | "urssafBeneficiaries"
        | "services"
        | "documents"
        | "titleSales"
        | "statements_bportal"
        | "titles_bportal"
        | "subscriptions"
        | "sageUploadHistory"
        | "avenants"
        | "maintenances";
    count: number;
    sticky?: boolean;
    portalType?: TPortalType;
};

const buttonStyle = {
    color: variables["secondary-purple"],
    minWidth: "unset",
    padding: "0px 8px",
    fontFamily: "Poppins",
    fontWeight: 500,
    "&:hover": { backgroundColor: variables["purple-20-op"] },
    "&:not([disabled])": { color: "#2D2D2D" },
};

const buttonSelectedStyle = {
    backgroundColor: "#F6F6F6",
};

const Pagination = ({
    page,
    rowsPerPage,
    currentPageSize = 0,
    onPageChange,
    onRowsPerPageChange,
    type,
    rowsPerPageOptions,
    count,
    sticky = false,
    portalType,
}: TProps) => {
    const [t] = useTranslation();

    const { companyInfos } = useCompanySettings();

    const currentPortal = useMemo(() => {
        if (portalType) return portalType;
        return companyInfos?.publisherType;
    }, [companyInfos, portalType]);

    const maxPage = Math.ceil(count / rowsPerPage);
    const diffMinMax = maxPage < 5 ? maxPage + 1 : 6;
    const numberArray = Array(maxPage)
        .fill(0)
        .map((e, i) => i);

    const paginationArray =
        page >= maxPage - diffMinMax
            ? numberArray.slice(-diffMinMax)
            : page < 3
              ? numberArray.slice(0, 0 + diffMinMax)
              : numberArray.slice(page - 3, page + 3);

    const paginationText = () => {
        let text = "";
        switch (type) {
            case "terms":
                text = t("Management.Terms");
                break;
            case "beneficiaries":
                text = t("Management.Beneficiary");
                break;
            case "titles":
                text = t("Management.Title");
                break;
            case "payment":
                text = t("Payment.PaymentTitle");
                break;
            case "paymentInfo":
                text = t("Payment.PaymentInfo");
                break;
            case "files":
                text = t("SendEmail.File");
                break;
            case "sales":
            case "titleSales":
                text = t("Summary.Sales");
                break;
            case "statements":
                text = t("Management.Statement");
                break;
            case "publications":
                text = t(`Publications.Publication_${currentPortal}`, { count: 1 });
                break;
            case "articles":
                text = t(`Articles.Article_${currentPortal}`);
                break;
            case "articles_audiovisual":
                text = t("PressAudio.Management.Article_audiovisual");
                break;
            case "logs":
                text = t("AdminPanel.Log");
                break;
            case "apiKeys":
                text = t("Settings.APIKey.Key");
                break;
            case "brands":
                text = t("Settings.Brand");
                break;
            case "contracts":
                text = t(`ContractValidation.RelatedResource_${currentPortal}`);
                break;
            case "users":
                text = t("Members.User");
                break;
            case "transferTerms":
                text = t("TransferTerms.Contract");
                break;
            case "transferTermAdvances":
                text = t("TransferTerms.Advance");
                break;
            case "transfereeTerms":
                text = t("TransferTerms.Contract");
                break;
            case "services":
                text = t("Settings.Service_one");
                break;
            case "documents":
                text = t("AddDocument.Document_one");
                break;
            case "sageUploadHistory":
                text = t("PaymentModal.SageHistory.File_one");
                break;
            case "maintenances":
                text = t("AdminPanel.Maintenance");
                break;
            default:
                text = t("Management.Element");
        }

        if (count > 1) {
            text = text + "s";
        }

        return text.toLowerCase();
    };

    return (
        <div className={`pagination-container${sticky ? " sticky" : ""}`}>
            <div className="pagination-left">
                <div style={{ marginRight: "4px" }}>
                    <StyledSelect
                        displayEmpty
                        fullWidth
                        value={rowsPerPage}
                        sx={{
                            ".MuiSelect-select": { paddingBlock: "6px !important" },
                        }}
                        renderValue={() => <span>{Math.min(currentPageSize, rowsPerPage)}</span>}
                        onChange={(ev) => onRowsPerPageChange(ev as any as ChangeEvent<HTMLInputElement>)}
                    >
                        {rowsPerPageOptions.map((opt, i) => (
                            <StyledMenuItem
                                sx={{ padding: "8px" }}
                                key={i}
                                value={opt}
                                className="pagination-left-options-items"
                            >
                                {opt}
                            </StyledMenuItem>
                        ))}
                    </StyledSelect>
                </div>
                <span>
                    {paginationText()}
                    <span style={{ marginLeft: "4px" }}>{t("Management.On")}</span>
                </span>
                <span>{count}</span>
            </div>
            <div className="pagination-right">
                <Button
                    disabled={page === 0}
                    sx={buttonStyle}
                    onClick={(e) => {
                        onPageChange(e, page - 1);
                    }}
                >
                    {"<"}
                </Button>
                {paginationArray.map((num, i) => {
                    return (
                        <Button
                            key={"pagination" + i}
                            className="pagination-btn"
                            disabled={page >= maxPage}
                            sx={num === page ? { ...buttonStyle, ...buttonSelectedStyle } : buttonStyle}
                            onClick={(e) => {
                                onPageChange(e, num);
                            }}
                        >
                            {num + 1}
                        </Button>
                    );
                })}
                <Button
                    disabled={page + 1 >= maxPage}
                    sx={buttonStyle}
                    onClick={(e) => {
                        onPageChange(e, page + 1);
                    }}
                >
                    {">"}
                </Button>
            </div>
        </div>
    );
};

export default Pagination;
